import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import 'pdfjs-dist/web/pdf_viewer.css';
import { pdfjsLib } from '../../helpers/pdfjs.ts';
import {
    applyReadOnly,
    computeHiddenFields,
    extractHideRules,
    extractPdfFieldValues,
    fillAndFlattenPdf,
    FillOptions,
    inspectFillablePdf,
    pdfRectToOverlayBox,
    pickInitialsFieldNames,
    PdfAnnotation,
    PdfHideRule,
    SignatureStamp,
} from '../../helpers/eicfFillablePdf.ts';
import {
    EICFFieldRole,
    FieldConfigMap,
    findMissingRequiredFields,
    parseFieldConfig,
} from '../../entities/EICFTemplates/fieldConfig.ts';
import { AdoptedSignature } from '../SignatureAdoptModal/SignatureAdoptModal.tsx';
import { AppMedError } from '../../error/AppMedError.ts';

const RENDER_SCALE = 1.3;

interface SignTarget {
    fieldName: string;
    kind: 'signature' | 'initials';
}

const noopLinkService = {
    externalLinkEnabled: false,
    getDestinationHash: () => '#',
    getAnchorUrl: () => '#',
    addLinkAttributes: () => {},
    isPageVisible: () => true,
    isPageCached: () => true,
} as unknown as Parameters<
    typeof pdfjsLib.AnnotationLayer.prototype.render
>[0]['linkService'];

/**
 * The per-role differences between the patient signer and the HCP counter-signer.
 * Everything else (pdf.js render, overlay tags, conditional visibility, signature
 * placement, required-field validation, fill+upload+submit) is shared below.
 */
export interface FillablePdfSigningConfig {
    /** Presigned URL of the PDF to render and sign. */
    sourceUrl: string | undefined;
    /** The template's per-field role/required/conditional config JSON. */
    fieldConfigJson: string | null | undefined;
    /** Role this signer fills; every other-role field is locked read-only. */
    editableRole: EICFFieldRole;
    /** Choose the signature field this signer signs (or null when there is none). */
    pickSignatureField: (
        signatureFieldNames: string[],
        config: FieldConfigMap,
    ) => string | null;
    /** Whether a signature field belongs to the OTHER party (excluded from initials). */
    isOtherPartySignature: (fieldName: string) => boolean;
    /** Patient: a signature must be placed. HCP: a click-only counter-sign is allowed. */
    requireSignaturePlacement: boolean;
    /** Flatten/read-only options for the final fill, given the placed field names. */
    resolveFillOptions: (
        config: FieldConfigMap,
        placedFieldNames: string[],
    ) => FillOptions;
    /** Upload the signed bytes and record the signature (role-specific endpoints). */
    submit: (signedBytes: Uint8Array) => Promise<void>;
    /** Called after a successful submission. */
    onSigned: () => void | Promise<void>;
    /** Effect dependencies (the source-dependent values that should re-trigger a load). */
    watch: unknown[];
}

export interface FillablePdfSigning {
    pagesRef: React.MutableRefObject<HTMLDivElement | null>;
    loading: boolean;
    submitting: boolean;
    error: string | null;
    modalOpen: boolean;
    closeModal: () => void;
    handleAdopt: (adopted: AdoptedSignature) => void;
    signaturePlaced: boolean;
    handleSubmit: () => Promise<void>;
}

export const useFillablePdfSigning = (
    config: FillablePdfSigningConfig,
): FillablePdfSigning => {
    const { t } = useTranslation();

    const pagesRef = useRef<HTMLDivElement | null>(null);
    const bytesRef = useRef<Uint8Array | null>(null);

    const pdfDocRef = useRef<any>(null);
    const signatureFieldRef = useRef<string | null>(null);
    const adoptedRef = useRef<AdoptedSignature | null>(null);
    const pendingFieldRef = useRef<string | null>(null);
    const targetsRef = useRef<Map<string, SignTarget>>(new Map());
    const tagElsRef = useRef<Map<string, HTMLElement>>(new Map());
    const placementsRef = useRef<Map<string, string>>(new Map());
    const fieldConfigRef = useRef<FieldConfigMap>({});
    // Field names the PDF itself marks Required — the source of truth for validation.
    const requiredFieldNamesRef = useRef<string[]>([]);
    // The PDF's own /Hide conditional rules + the rendered widget <section> per field,
    // used to gate fields exactly as the document's form logic dictates.
    const hideRulesRef = useRef<PdfHideRule[]>([]);
    const fieldSectionsRef = useRef<Map<string, HTMLElement>>(new Map());

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [placedFields, setPlacedFields] = useState<string[]>([]);

    const placeOnField = (fieldName: string, adopted: AdoptedSignature) => {
        const target = targetsRef.current.get(fieldName);
        if (!target) return;
        const png =
            target.kind === 'initials' ? adopted.initialsPng : adopted.signaturePng;
        placementsRef.current.set(fieldName, png);

        const el = tagElsRef.current.get(fieldName);
        if (el) {
            el.replaceChildren();
            el.style.background = 'transparent';
            el.style.border = 'none';
            const img = document.createElement('img');
            img.src = png;
            img.style.width = '100%';
            img.style.height = '100%';
            img.style.objectFit = 'contain';
            el.appendChild(img);
        }
        setPlacedFields((prev) =>
            prev.includes(fieldName) ? prev : [...prev, fieldName],
        );
    };

    const handleTagClick = (fieldName: string) => {
        pendingFieldRef.current = fieldName;
        if (adoptedRef.current) placeOnField(fieldName, adoptedRef.current);
        else setModalOpen(true);
    };

    const handleAdopt = (adopted: AdoptedSignature) => {
        adoptedRef.current = adopted;
        setModalOpen(false);
        if (pendingFieldRef.current)
            placeOnField(pendingFieldRef.current, adopted);
    };

    const collectFieldValues = async () => {
        const doc = pdfDocRef.current;
        const annotations: PdfAnnotation[] = [];
        for (let n = 1; n <= doc.numPages; n += 1) {

            const page = await doc.getPage(n);

            const pageAnnotations = await page.getAnnotations({
                intent: 'display',
            });

            pageAnnotations.forEach((a: any) =>
                annotations.push({
                    id: a.id,
                    fieldName: a.fieldName,
                    fieldType: a.fieldType,
                    // Radios report buttonValue, checkboxes exportValue; needed because
                    // pdf.js stores a selected radio as boolean true, losing the option.
                    exportValue: a.buttonValue ?? a.exportValue,
                }),
            );
        }
        const storageAll: Record<string, { value: unknown }> = {};

        for (const [id, value] of doc.annotationStorage as Iterable<[string, any]>)
            storageAll[id] = value;
        return extractPdfFieldValues(annotations, storageAll);
    };

    // Honour the PDF's own /Hide conditional rules: hide a field's tag + rendered
    // widget whenever the document's form logic would hide it for the current answers.
    const refreshConditionalVisibility = async () => {
        if (!pdfDocRef.current || hideRulesRef.current.length === 0) return;
        const values = await collectFieldValues();
        const hidden = computeHiddenFields(hideRulesRef.current, values);
        tagElsRef.current.forEach((tag, name) => {
            tag.style.display = hidden.has(name) ? 'none' : 'flex';
        });
        fieldSectionsRef.current.forEach((section, name) => {
            section.style.display = hidden.has(name) ? 'none' : '';
        });
    };

    useEffect(() => {
        let cancelled = false;
        const refresh = refreshConditionalVisibility;
        const containerNode = pagesRef.current;

        const buildTag = (
            pageBox: HTMLElement,
            target: SignTarget,
            box: { left: number; top: number; width: number; height: number },
        ) => {
            const tag = document.createElement('div');
            tag.style.position = 'absolute';
            tag.style.left = `${box.left}px`;
            tag.style.top = `${box.top}px`;
            tag.style.width = `${box.width}px`;
            tag.style.height = `${box.height}px`;
            tag.style.cursor = 'pointer';
            // pdf.js gives each form-widget section an incrementing z-index
            // (0, 2, 4, …), so a text field's input can outrank a low value and
            // paint over the tag. Sit above every widget so initials/signature
            // boxes stay clickable.
            tag.style.zIndex = '1000';
            tag.style.display = 'flex';
            tag.style.alignItems = 'center';
            tag.style.justifyContent = 'center';
            tag.style.background = 'rgba(255, 214, 0, 0.35)';
            tag.style.border = '1px dashed #b59b00';
            tag.style.fontSize = '11px';
            tag.style.color = '#6b5d00';
            tag.textContent =
                target.kind === 'initials'
                    ? t('eicf.pdf.tagInitials')
                    : t('eicf.pdf.tagSign');
            tag.addEventListener('click', () => handleTagClick(target.fieldName));
            pageBox.appendChild(tag);
            tagElsRef.current.set(target.fieldName, tag);
        };

        const load = async () => {
            setError(null);
            setLoading(true);
            try {
                if (!config.sourceUrl) throw new Error(t('eicf.pdf.loadError'));
                const response = await fetch(config.sourceUrl);
                if (!response.ok) throw new Error(t('eicf.pdf.loadError'));
                const buffer = await response.arrayBuffer();
                if (cancelled) return;

                bytesRef.current = new Uint8Array(buffer.slice(0));

                // Per-field roles: lock every field this signer does not own.
                const cfg = parseFieldConfig(config.fieldConfigJson);
                fieldConfigRef.current = cfg;
                const readOnlyNames = Object.entries(cfg)
                    .filter(([, rule]) => rule.role !== config.editableRole)
                    .map(([name]) => name);
                const renderBytes = readOnlyNames.length
                    ? await applyReadOnly(bytesRef.current, readOnlyNames)
                    : new Uint8Array(buffer);
                if (cancelled) return;

                // Discover the signature + initials targets and their boxes from a
                // single parse (names, types and widget rects at once).
                const inspected = await inspectFillablePdf(bytesRef.current);
                const sigNames = inspected
                    .filter((f) => f.type === 'signature')
                    .map((f) => f.name);
                const allNames = inspected.map((f) => f.name);
                const rects = new Map(inspected.map((f) => [f.name, f.rect]));
                // Required-ness is the PDF's own; the admin config only assigns roles.
                requiredFieldNamesRef.current = inspected
                    .filter((f) => f.required)
                    .map((f) => f.name);
                // Conditional gating is the PDF's own /Hide actions, not admin metadata.
                hideRulesRef.current = await extractHideRules(bytesRef.current);
                if (cancelled) return;

                const signatureField = config.pickSignatureField(sigNames, cfg);
                signatureFieldRef.current = signatureField;
                const initialsNames = pickInitialsFieldNames(allNames, sigNames, [
                    ...(signatureField ? [signatureField] : []),
                    ...sigNames.filter(config.isOtherPartySignature),
                ]);

                targetsRef.current = new Map();
                if (signatureField)
                    {targetsRef.current.set(signatureField, {
                        fieldName: signatureField,
                        kind: 'signature',
                    });}
                initialsNames.forEach((n) =>
                    targetsRef.current.set(n, { fieldName: n, kind: 'initials' }),
                );
                if (cancelled) return;

                const doc = await pdfjsLib.getDocument({ data: renderBytes })
                    .promise;
                if (cancelled) return;
                pdfDocRef.current = doc;

                const container = pagesRef.current;
                if (!container) return;
                container.replaceChildren();
                tagElsRef.current = new Map();
                fieldSectionsRef.current = new Map();

                for (let n = 1; n <= doc.numPages; n += 1) {

                    const page = await doc.getPage(n);
                    if (cancelled) return;
                    const viewport = page.getViewport({ scale: RENDER_SCALE });

                    const pageBox = document.createElement('div');
                    pageBox.style.position = 'relative';
                    pageBox.style.margin = '0 auto 16px';
                    pageBox.style.width = `${viewport.width}px`;
                    pageBox.style.height = `${viewport.height}px`;
                    pageBox.style.boxShadow = '0 0 4px rgba(0,0,0,0.2)';

                    const canvas = document.createElement('canvas');
                    canvas.width = viewport.width;
                    canvas.height = viewport.height;
                    pageBox.appendChild(canvas);
                    container.appendChild(pageBox);

                    await page.render({
                        canvasContext: canvas.getContext('2d'),
                        viewport,
                    }).promise;

                    const annotations = await page.getAnnotations({
                        intent: 'display',
                    });
                    const layerDiv = document.createElement('div');
                    layerDiv.className = 'annotationLayer';
                    layerDiv.style.position = 'absolute';
                    layerDiv.style.inset = '0';
                    pageBox.appendChild(layerDiv);

                    const annotationLayer = new pdfjsLib.AnnotationLayer({
                        div: layerDiv,
                        page,
                        viewport: viewport.clone({ dontFlip: true }),
                        linkService: noopLinkService,
                        annotationStorage: doc.annotationStorage,
                        accessibilityManager: null,
                        annotationCanvasMap: null,
                        annotationEditorUIManager: null,
                        structTreeLayer: null,
                        commentManager: null,
                    });

                    await annotationLayer.render({
                        viewport: viewport.clone({ dontFlip: true }),
                        div: layerDiv,
                        annotations,
                        page,
                        linkService: noopLinkService,
                        annotationStorage: doc.annotationStorage,
                        renderForms: true,
                    });

                    // Map each rendered widget section to its field so /Hide-gated
                    // fields can be hidden until their controller selects the right value.
                    const fieldNameById = new Map(
                        annotations.map((a: PdfAnnotation) => [a.id, a.fieldName]),
                    );
                    layerDiv
                        .querySelectorAll('[data-annotation-id]')
                        .forEach((section) => {
                            const id = section.getAttribute('data-annotation-id');
                            const fieldName = id ? fieldNameById.get(id) : undefined;
                            if (fieldName)
                                {fieldSectionsRef.current.set(
                                    fieldName,
                                    section as HTMLElement,
                                );}
                        });

                    // Overlay the signature/initials tags for fields on this page.
                    targetsRef.current.forEach((target) => {
                        const rect = rects.get(target.fieldName);
                        if (!rect || rect.pageIndex !== n - 1) return;
                        buildTag(
                            pageBox,
                            target,
                            pdfRectToOverlayBox(rect, viewport.height, RENDER_SCALE),
                        );
                    });
                }

                container.addEventListener('input', refresh);
                container.addEventListener('change', refresh);
                await refresh();

                if (!cancelled) setLoading(false);
            } catch (e) {
                if (cancelled) return;
                setError(e instanceof Error ? e.message : t('eicf.pdf.loadError'));
                setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
            if (containerNode) {
                containerNode.removeEventListener('input', refresh);
                containerNode.removeEventListener('change', refresh);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, config.watch);

    const handleSubmit = async () => {
        setError(null);
        const signatureField = signatureFieldRef.current;
        const missingPlacement = signatureField
            ? !placementsRef.current.has(signatureField)
            : config.requireSignaturePlacement;
        if (missingPlacement) {
            setError(t('eicf.pdf.signatureRequired'));
            return;
        }
        if (!bytesRef.current || !pdfDocRef.current) {
            setError(t('eicf.pdf.loadError'));
            return;
        }

        setSubmitting(true);
        try {
            const values = await collectFieldValues();

            // A field the PDF currently hides (e.g. the "no" initials when "yes" is
            // chosen) is neither required nor filled/stamped — follow the document.
            const hidden = computeHiddenFields(hideRulesRef.current, values);
            const placedNames = Array.from(placementsRef.current.keys()).filter(
                (name) => !hidden.has(name),
            );
            const missing = findMissingRequiredFields(
                fieldConfigRef.current,
                values,
                placedNames,
                requiredFieldNamesRef.current.filter((name) => !hidden.has(name)),
                config.editableRole,
            );
            if (missing.length > 0) {
                setError(
                    t('eicf.pdf.missingRequired'),
                );
                setSubmitting(false);
                return;
            }

            const visibleValues = Object.fromEntries(
                Object.entries(values).filter(([name]) => !hidden.has(name)),
            );
            const signatures: SignatureStamp[] = Array.from(
                placementsRef.current.entries(),
            )
                .filter(([fieldName]) => !hidden.has(fieldName))
                .map(([fieldName, pngDataUrl]) => ({ fieldName, pngDataUrl }));

            const signedBytes = await fillAndFlattenPdf(
                bytesRef.current,
                visibleValues,
                signatures,
                config.resolveFillOptions(fieldConfigRef.current, placedNames),
            );

            await config.submit(signedBytes);
            await config.onSigned();
        } catch (e) {
            if (e instanceof AppMedError) setError(e.message);
            else if (e instanceof Error) setError(e.message);
            else setError(t('eicf.pdf.unexpectedError'));
        } finally {
            setSubmitting(false);
        }
    };

    const signatureField = signatureFieldRef.current;
    const signaturePlaced = signatureField
        ? placedFields.includes(signatureField)
        : !config.requireSignaturePlacement;

    return {
        pagesRef,
        loading,
        submitting,
        error,
        modalOpen,
        closeModal: () => setModalOpen(false),
        handleAdopt,
        signaturePlaced,
        handleSubmit,
    };
};
